// src/components/WishlistPage.jsx

import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useNavigate } from "react-router-dom";
import BackButton from "./BackButton";
import { getUserProfile, isAuthenticated } from "../utils/auth";

const Icon = ({ d, className = "w-3.5 h-3.5", strokeWidth = 1.8, fill = "none" }) => (
  <svg
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d={d} />
  </svg>
);

const HEART =
  "M19.5 12.6 12 20l-7.5-7.4A4.9 4.9 0 0 1 12 6.1a4.9 4.9 0 0 1 7.5 6.5Z";
const BAG = "M4 8h16l-1.3 12.2a2 2 0 0 1-2 1.8H7.3a2 2 0 0 1-2-1.8ZM9 8V6a3 3 0 0 1 6 0v2";
const LOCK = "M6 10h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2ZM8 10V7a4 4 0 0 1 8 0v3";

// Shared shell for the empty / signed-out / error states.
function EmptyState({ glyph, title, children, action }) {
  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8 ob-viewin">
      <BackButton />
      <div className="ob-empty">
        <div className="ob-empty-glyph">
          <Icon d={glyph} className="w-6 h-6" strokeWidth={1.6} />
        </div>
        <div className="ob-display text-[27px]" style={{ color: "var(--ob-ink)" }}>
          {title}
        </div>
        <p className="text-[13px] mt-2 max-w-[360px] mx-auto leading-relaxed">
          {children}
        </p>
        {action}
      </div>
    </div>
  );
}

export default function WishlistPage() {
  const { addToCart } = useCart();
  // The list is the server's, mirrored by WishlistContext (hydrated on
  // sign-in, updated by every manual call and agent turn); the skeleton shows
  // while that first load is in flight.
  const { wishlistItems, wishlistLoading: loading, removeFromWishlist } = useWishlist();
  const navigate = useNavigate();
  const profile = getUserProfile();
  const customerId = profile?.customer_id ? String(profile.customer_id) : null;
  const isLoggedIn = isAuthenticated() && customerId;

  // ✅ Utility to safely format price
  const formatPrice = (price) => {
    const num = typeof price === "string" ? parseFloat(price) : price;
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  // Add to cart, then remove from wishlist — context handles state update optimistically
  const handleAddToCart = (item) => {
    addToCart({
      ...item,
      quantity: 1,
      size: item.size || null,
    });
    const id = item.article_id ?? item.id;
    if (id) {
      removeFromWishlist(id);
    }
  };

  // Remove from wishlist — context handles state update optimistically
  const handleRemoveFromWishlist = (item) => {
    const id = item.article_id ?? item.id;
    if (!id) return;
    removeFromWishlist(id);
  };

  // ✅ Handle product click - navigate to product detail
  const handleProductClick = (item) => {
    const id = item.id ?? item.article_id;
    if (id) {
      navigate(`/item/${id}`);
    }
  };

  // Not logged in: show sign-in message
  if (!isLoggedIn && !loading) {
    return (
      <EmptyState
        glyph={LOCK}
        title="Sign in to see your wishlist"
        action={
          <button
            type="button"
            onClick={() => navigate("/")}
            className="ob-btn ob-btn-solid ob-btn-lg mt-6"
          >
            Continue to home
          </button>
        }
      >
        Your wishlist is saved to your account, so it follows you across devices.
      </EmptyState>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8">
        <div className="ob-skel h-[30px] w-[70px] rounded-[10px]" />
        <div className="ob-skel h-[44px] w-[240px] rounded-[12px] mt-5 mb-6" />
        <div className="ob-grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i}>
              <div className="ob-skel rounded-[16px]" style={{ aspectRatio: "3 / 4" }} />
              <div className="ob-skel h-3 w-3/4 rounded mt-3" />
              <div className="ob-skel h-3 w-1/3 rounded mt-2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (wishlistItems.length === 0) {
    return (
      <EmptyState
        glyph={HEART}
        title="Your wishlist is empty"
        action={
          <button
            type="button"
            onClick={() => navigate("/")}
            className="ob-btn ob-btn-solid ob-btn-lg mt-6"
          >
            Continue shopping
          </button>
        }
      >
        Tap the heart on anything you love, or just tell Orbiee{" "}
        <b style={{ color: "var(--ob-ink-2)" }}>&ldquo;save this for later&rdquo;</b>.
      </EmptyState>
    );
  }

  const count = wishlistItems.length;

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8 ob-viewin">
      <BackButton />

      <div className="flex items-end justify-between gap-4 mb-6 mt-2">
        <div>
          <span className="ob-eyebrow">Saved for later</span>
          <h1 className="ob-display text-[clamp(1.8rem,4.2vw,2.5rem)] leading-[1.08] mt-1">
            Your wishlist
          </h1>
        </div>
        <span className="ob-chip shrink-0">
          {count} item{count === 1 ? "" : "s"}
        </span>
      </div>

      <div className="ob-grid ob-stagger">
        {wishlistItems.map((item) => {
          const title = item.name || item.title || "";
          return (
            <div
              key={item.id}
              className="ob-card"
              onClick={() => handleProductClick(item)}
            >
              <div className="ob-shot">
                <img
                  src={item.image || item.image_url}
                  alt={title}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    // Let the sunk background show instead of a broken-image icon.
                    e.currentTarget.style.visibility = "hidden";
                  }}
                />

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFromWishlist(item);
                  }}
                  className="ob-fav ob-on"
                  title="Remove from wishlist"
                  aria-label={`Remove ${title} from wishlist`}
                >
                  <Icon d={HEART} className="w-4 h-4" fill="currentColor" strokeWidth={1.6} />
                </button>
              </div>

              <div className="ob-card-meta">
                {item.brand && <div className="ob-card-brand">{item.brand}</div>}
                <div className="ob-card-title" title={title}>
                  {title}
                </div>
                <div className="ob-card-price ob-tabnum">
                  {item.currency || "USD"} {formatPrice(item.price)}
                </div>

                <div className="ob-card-actions">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(item);
                    }}
                    className="ob-btn ob-btn-ghost ob-btn-sm ob-btn-block"
                  >
                    <Icon d={BAG} className="w-[13px] h-[13px]" />
                    Move to bag
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
